import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "menu", "search", "option", "group", "empty", "hiddenInput", "label"]

  connect() {
    console.log(`[ad-account-picker] connect: ${this.optionTargets.length} options in ${this.groupTargets.length} groups`)
    this.highlightIndex = -1
    this._closeOnOutside = this._closeOnOutside.bind(this)
    this._syncLabelFromHiddenInput()
  }

  disconnect() {
    document.removeEventListener("click", this._closeOnOutside)
  }

  toggle(event) {
    event?.stopPropagation()
    if (this.menuTarget.classList.contains("hidden")) this.open()
    else this.close()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    document.addEventListener("click", this._closeOnOutside)
    setTimeout(() => {
      if (this.hasSearchTarget) this.searchTarget.focus()
    }, 10)
  }

  close() {
    this.menuTarget.classList.add("hidden")
    document.removeEventListener("click", this._closeOnOutside)
    this.highlightIndex = -1
    this._refreshHighlight()
  }

  selectAccount(event) {
    const value = event.currentTarget.dataset.value
    const label = event.currentTarget.dataset.label
    if (value) {
      this.hiddenInputTarget.value = value
      this.labelTarget.textContent = label || value
      this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.close()
    this.buttonTarget?.focus()
  }

  filter(event) {
    const q = (event.target.value || "").trim().toLowerCase()
    let firstVisible = -1
    this.optionTargets.forEach((opt, i) => {
      const blob = (opt.dataset.search || "").toLowerCase()
      const visible = q === "" || blob.includes(q)
      opt.classList.toggle("hidden", !visible)
      if (visible && firstVisible === -1) firstVisible = i
    })
    // Hide business group sections whose options are ALL filtered out — with
    // many businesses the leftover headers made the list look unfiltered
    // (78 accounts / ~40 headers: typing appeared to do nothing).
    this.groupTargets.forEach((group) => {
      const anyVisibleInGroup = !!group.querySelector(
        "[data-ad-account-picker-target='option']:not(.hidden)"
      )
      group.classList.toggle("hidden", !anyVisibleInGroup)
    })
    this.highlightIndex = firstVisible
    this._refreshHighlight()
    if (this.hasEmptyTarget) {
      const anyVisible = this.optionTargets.some(o => !o.classList.contains("hidden"))
      this.emptyTarget.classList.toggle("hidden", anyVisible)
    }
    console.log(`[ad-account-picker] filter "${q}": ${this.optionTargets.filter(o => !o.classList.contains("hidden")).length}/${this.optionTargets.length} options visible`)
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.buttonTarget?.focus()
      return
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const visible = this.optionTargets.filter(o => !o.classList.contains("hidden"))
      if (visible.length === 0) return
      const cur = visible.indexOf(this.optionTargets[this.highlightIndex])
      const next = event.key === "ArrowDown"
        ? (cur + 1) % visible.length
        : (cur - 1 + visible.length) % visible.length
      this.highlightIndex = this.optionTargets.indexOf(visible[next])
      this._refreshHighlight()
      visible[next].scrollIntoView({ block: "nearest" })
      return
    }
    if (event.key === "Enter") {
      event.preventDefault()
      if (this.highlightIndex >= 0) {
        this.optionTargets[this.highlightIndex].click()
      }
    }
  }

  _syncLabelFromHiddenInput() {
    if (this.hiddenInputTarget.value && this.hasLabelTarget) {
      const matched = this.optionTargets.find(
        (o) => o.dataset.value === this.hiddenInputTarget.value,
      )
      if (matched) {
        this.labelTarget.textContent = matched.dataset.label
      }
    }
  }

  _refreshHighlight() {
    this.optionTargets.forEach((opt, i) => {
      opt.toggleAttribute("data-highlighted", i === this.highlightIndex)
    })
  }

  _closeOnOutside(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }
}
